/**
 * A12b — Onboarding Step 4/4: Tienda de Celulares (bundle cerrado)
 *
 * Read-only view of the 8 included modules with "Incluido" badges.
 * On submit: INSERT bundle preset into empresa_modulos, then redirect
 * to the celulares-specific wizard (VT01-03).
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  Package,
  CreditCard,
  Radio,
  Smartphone,
  BarChart3,
  User,
  Settings,
  Info,
  CheckCircle,
} from 'lucide-react';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { supabase } from '@shared/lib/supabaseClient';
import OnboardingLayout from '../../components/OnboardingLayout';
import { useOnboarding } from '../../contexts/OnboardingContext';

// ── Bundle modules ───────────────────────────────────────────────────────────

interface BundleModule {
  icon: React.FC<{ className?: string }>;
  name: string;
  desc: string;
}

const BUNDLE_MODULES: BundleModule[] = [
  {
    icon: Receipt,
    name: 'POS y ventas',
    desc: 'Punto de venta con planes de credito preconfigurados. Precio contado vs financiado.',
  },
  {
    icon: Package,
    name: 'Inventario',
    desc: 'Equipos, variantes (color/almacenamiento) y stock en tiempo real.',
  },
  {
    icon: CreditCard,
    name: 'Credito Movil',
    desc: 'Financiamientos, cuotas, mora y bloqueo automatico a los N dias sin pago.',
  },
  {
    icon: Radio,
    name: 'Enrollment MDM',
    desc: 'Instalacion de apps via QR. Bloqueo por IMEI. 5 APKs de proteccion.',
  },
  {
    icon: Smartphone,
    name: 'Portal del cliente',
    desc: 'App que ve el cliente: cuotas, historial de pagos y como pagar.',
  },
  {
    icon: BarChart3,
    name: 'Reportes de cobranza',
    desc: 'Recaudacion mensual, cartera en mora, ranking de equipos.',
  },
  {
    icon: User,
    name: 'Clientes',
    desc: 'Perfil con historial de compras, score interno y consulta crediticia.',
  },
  {
    icon: Settings,
    name: 'Configuracion tienda',
    desc: 'Datos bancarios, dias de gracia, planes de credito y WhatsApp Business.',
  },
];

const CELULARES_MODULE_KEYS = [
  'pos',
  'inventario',
  'credito_movil',
  'enrollment_mdm',
  'portal_cliente',
  'reportes_cobranza',
  'clientes',
  'configuracion_tienda',
];

// ── Component ────────────────────────────────────────────────────────────────

const CelularesBundlePage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useOnboarding();
  const { empresaId } = state;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleNext = async () => {
    setError(null);
    setLoading(true);

    try {
      // Insert all bundle modules as active
      if (empresaId) {
        const rows = CELULARES_MODULE_KEYS.map((modKey) => ({
          empresa_id: empresaId,
          modulo: modKey,
          activo: true,
        }));

        const { error: dbErr } = await (supabase as any)
          .from('empresa_modulos')
          .upsert(rows, { onConflict: 'empresa_id,modulo' });

        if (dbErr) {
          console.error('[CelularesBundle] Upsert error:', dbErr);
          // Non-blocking
        }

        // Mark onboarding as complete and set bundle type
        await (supabase as any)
          .from('empresas')
          .update({
            onboarding_completado: true,
            tipo_negocio: 'celulares',
            bundle_activo: 'celulares',
          })
          .eq('id', empresaId);
      }

      // Redirect to celulares-specific wizard (VT01-03)
      navigate('/onboarding/celulares/tienda');
    } catch (err: any) {
      setError(err?.message || 'Error inesperado.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    navigate('/onboarding/celulares/tienda');
  };

  return (
    <OnboardingLayout
      step={4}
      onBack={() => navigate('/onboarding/negocio')}
      onNext={handleNext}
      nextLabel="Configurar mi tienda"
      loading={loading}
      skipLabel="Configurar despues"
      onSkip={handleSkip}
      contentMaxWidth={840}
    >
      {/* Header with icon + title + badge */}
      <div className="flex items-center gap-3.5 mb-4">
        <Smartphone className="w-9 h-9 text-primary" />
        <div>
          <h1 className="text-3xl font-bold tracking-tight mb-1.5">Tienda de Celulares</h1>
          <span className="font-mono text-[10px] font-bold uppercase tracking-wider bg-primary text-primary-foreground px-2.5 py-1 rounded inline-block">
            Bundle especializado - 8 modulos incluidos
          </span>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-md px-3.5 py-2.5 text-[13px] text-blue-700 flex gap-2 mb-5">
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>
          Este bundle viene completo -- todos los modulos estan incluidos y preconfigurados.{' '}
          <strong>No se pueden desactivar modulos individuales</strong> ni combinar con modulos de otros verticales.
          El wizard de configuracion de la tienda arranca justo despues.
        </span>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-2">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Module cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
        {BUNDLE_MODULES.map((mod) => {
          const Icon = mod.icon;
          return (
            <div
              key={mod.name}
              className="flex gap-3 items-start p-3 bg-card border border-border rounded-lg"
            >
              <Icon className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="font-semibold text-[13.5px]">{mod.name}</span>
                  <span className="font-mono text-[8.5px] font-bold bg-green-50 text-green-700 border border-green-200 rounded px-1.5 py-0.5 flex items-center gap-1">
                    <CheckCircle className="w-2.5 h-2.5" /> Incluido
                  </span>
                </div>
                <p className="text-[12px] text-muted-foreground leading-snug">{mod.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Next step hint */}
      <div className="bg-primary/5 border border-primary/20 rounded-md px-3.5 py-3 text-[13px] text-muted-foreground">
        <strong className="text-primary">Siguiente paso:</strong>{' '}
        wizard de configuracion de la tienda -- 3 pasos: datos del negocio, cuenta bancaria para transferencias y WhatsApp Business para notificaciones automaticas.
      </div>
    </OnboardingLayout>
  );
};

export default CelularesBundlePage;
